import React, { useState, useEffect } from "react";
import "./DashboardRightSection.css";
import { getAllVotesWithFilters, submitVote, likeDislike } from "../../api/Vote";
import { getToken, getUserId } from "../../api/index";
import { useNavigate } from "react-router-dom";
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import Loader from "../Loader";

let cachedVotes = null;
let cachedCurrentIndex = 0;

function DashboardRightSection({ initialVotes, isInitialLoading, refreshTrigger }) {
  const [allVotes, setAllVotes] = useState(() => {
    if (initialVotes && initialVotes.length > 0) return initialVotes;
    return cachedVotes || false;
  });
  const userId = getUserId();
  const [isLoading, setIsLoading] = useState(() => {
    if (initialVotes && initialVotes.length > 0) return false;
    if (cachedVotes) return false;
    return isInitialLoading;
  });
  const [currentIndex, setCurrentIndex] = useState(cachedCurrentIndex);
  const navigate = useNavigate();

  const handleAllVotes = async () => {
    if (isLoading && !cachedVotes) return;
    setIsLoading(true);
    const token = getToken();
    try {
      const response = await getAllVotesWithFilters(
        {
          userId: userId,
          skip: 0,
          take: 5,
        },
        token,
        true // marks this as a static/sidebar request
      );
      setAllVotes(response);
      cachedVotes = response;
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initialVotes && initialVotes.length > 0) {
      setAllVotes(initialVotes);
      cachedVotes = initialVotes;
      setIsLoading(false);
    }
  }, [initialVotes]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      handleAllVotes();
    }
  }, [refreshTrigger]);

  useEffect(() => {
    if (!initialVotes || initialVotes.length === 0) {
      if (!cachedVotes) {
        handleAllVotes();
      }
    }
  }, []);

  const handleVoteSubmit = async (voteId, optionId) => {
    const userId = getUserId();
    const token = getToken();
    try {
      const response = await submitVote({ voteId, userId, optionId }, token);
      console.log("Submit vote response in sidebar:", response);
      
      setAllVotes(prevVotes => {
        const updated = prevVotes.map(vote => {
          if (vote.voteId === voteId) {
            const previouslySelected = vote.selectedOptionId;
            const updatedOptions = vote.options.map(option => {
              let count = option.voteCount;
              if (option.id === optionId) {
                count += 1;
              }
              if (previouslySelected && option.id === previouslySelected) {
                count = Math.max(0, count - 1);
              }
              return {
                ...option,
                voteCount: count,
                isSelected: option.id === optionId
              };
            });

            const totalVotes = updatedOptions.reduce((sum, opt) => sum + opt.voteCount, 0);
            const finalOptions = updatedOptions.map(opt => ({
              ...opt,
              percentage: totalVotes === 0 ? 0 : Math.round((opt.voteCount / totalVotes) * 100)
            }));

            return {
              ...vote,
              isVoted: true,
              selectedOptionId: optionId,
              options: finalOptions
            };
          }
          return vote;
        });
        cachedVotes = updated;
        return updated;
      });
    } catch (error) {
      console.error("Error submitting vote from sidebar:", error);
    }
  };


  const handleNextVote = () => {
    if (allVotes && currentIndex < allVotes.length - 1) {
      setCurrentIndex(prev => {
        const nextIdx = prev + 1;
        cachedCurrentIndex = nextIdx;
        return nextIdx;
      });
    }
  };

  const handlePrevVote = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => {
        const prevIdx = prev - 1;
        cachedCurrentIndex = prevIdx;
        return prevIdx;
      });
    }
  };

  const handleLikeDislike = async (voteId) => {
    const userId = getUserId();
    const token = getToken();
    try {
      const response = await likeDislike({ voteId, userId }, token);
      console.log("Like/dislike response in sidebar:", response);
      setAllVotes(prevVotes => {
        const updated = prevVotes.map(vote => {
          if (vote.voteId === voteId) {
            return {
              ...vote,
              isLiked: !vote.isLiked,
              likeCount: vote.isLiked ? Math.max(0, vote.likeCount - 1) : vote.likeCount + 1
            };
          }
          return vote;
        });
        cachedVotes = updated;
        return updated;
      });
    } catch (error) {
      console.error("Error liking/disliking vote from sidebar:", error);
    }
  };

  const handleCommentClick = (voteId) => {
    navigate(`/vote?voteId=${voteId}`);
  };


  const handleDownloadGoogleAppClick = () => {
    window.open("https://play.google.com/store/apps/details?id=com.amalgamate", "_blank");
  }

  const handleDownloadAppleAppClick = () => {
    window.open("https://apps.apple.com/in/app/amalgamate/id6744918457", "_blank");
  }

  const getBackgroundColor = (initials) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
      '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
      '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA'
    ];

    let hash = 0;
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    return colors[hash % colors.length];
  };

  const getUserInitials = (userName) => {
    if (!userName) return '';
    const words = userName.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    const firstInitial = words[0] ? words[0].charAt(0).toUpperCase() : '';
    const lastInitial = words[words.length - 1] ? words[words.length - 1].charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
  };

  return (
    <div className="DashboardRightSection">
      <div className="dashboardRightSectionContainer">
        <div className="downloadAppContainer">
          <span className="downloadAppText">Download App</span>
          <div className="smallTextContainer">
            <span className="downloadAppText2">
              We are dedicated to pushing the boundaries of what
            </span>
            <span className="downloadAppText2">can be achieved.</span>
          </div>
          <div className="downloadAppCards">
            <div className="downloadAppCardContainer">
              <div className="downloadAppCard" onClick={handleDownloadGoogleAppClick}>
                <img
                  src="/Assets/LandingPage/playStoreIcon.svg"
                  alt="no img found"
                  className="downloadAppCardIcon"
                />
                <div className="downloadAppCardTextContainer">
                  <span className="downloadAppCardText1">Get it on</span>
                  <span className="downloadAppCardText2">
                    Google Play
                  </span>
                </div>
              </div>
              <div className="downloadAppCard" onClick={handleDownloadAppleAppClick}>
                <img
                  src="/Assets/LandingPage/appStoreIcon.svg"
                  alt="no img found"
                  className="downloadAppCardIcon2"
                />
                <div className="downloadAppCardTextContainer">
                  <span className="downloadAppCardText1">
                    Get it on
                  </span>
                  <span className="downloadAppCardText2">App Store</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="recentVoteContainer">
          <span className="recentVoteText">Recent Votes</span>
          {allVotes && allVotes.length > 0 ? (
            (() => {
              const item = allVotes[currentIndex];
              if (!item) return null;
              return (
                <div className="recentVoteCardContainer">
                  <div className="recentVoteCardHeaderContainer">
                    {item.createdByUserPhoto ? (
                      <img
                        src={item.createdByUserPhoto}
                        alt="no-img-found"
                        className="headerProfilePic"
                      />
                    ) : (
                      <div
                        className="recentVoteProfilePic"
                        style={{
                          backgroundColor: getBackgroundColor(getUserInitials(item?.displayName))
                        }}
                      >
                        {getUserInitials(item?.displayName ? item.displayName : item.createdByUserName)}
                      </div>
                    )}
                    <div className="recentVoteCardHeaderTextContainer">
                      <span className="recentVoteCardHeaderText1">{item.displayName ? item.displayName : item.createdByUserName}</span>
                    </div>
                  </div>
                  <div className="recentVoteTitleContainer">
                    <span className="recentVoteTitleText">{item.title}</span>
                  </div>
                  
                  {item.options && item.options.length > 0 && (
                    <div className="recentVoteOptionsScrollable">
                      {item.isVoted ? (
                        <div className="recentVoteResults">
                          {item.options.map((option) => (
                            <div key={option.id} className={`recentVoteProgressWrapper ${option.id === item.selectedOptionId ? 'selected' : ''}`}>
                              <div 
                                className="recentVoteProgressBar" 
                                style={{ width: `${option.percentage}%` }}
                              />
                              <div className="recentVoteProgressContent">
                                <span className="optionValueText">
                                  {option.value} {option.id === item.selectedOptionId && "✔️"}
                                </span>
                                  <div className="optionPercentOrChange">
                                    <span className="optionPercentText">{option.percentage}%</span>
                                  </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="recentVoteActionButtons">
                          {item.options.map((option) => (
                            <button
                              key={option.id}
                              className="recentVoteOptionBtn"
                              onClick={() => handleVoteSubmit(item.voteId, option.id)}
                              disabled={item.isPaused}
                            >
                              {option.value}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="recentVoteCardFooter">
                    <div className="recentVoteCardActions">
                      <div 
                        className={`recentVoteCardActionContainer ${item.isLiked ? 'liked' : ''}`} 
                        onClick={() => handleLikeDislike(item.voteId)}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.isLiked ? (
                          <FavoriteRoundedIcon sx={{ color: "#FF0000", fontSize: { xs: "12px", sm: "14px", md: "16px", lg: "18px" } }} />
                        ) : (
                          <FavoriteBorderRoundedIcon sx={{ color: "#fff", fontSize: { xs: "12px", sm: "14px", md: "16px", lg: "18px" } }} />
                        )}
                        <span className="feedCardFooterText">{item.likeCount}</span>
                      </div>
                      <div 
                        className="recentVoteCardActionContainer" 
                        onClick={() => handleCommentClick(item.voteId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <ChatBubbleOutlineRoundedIcon sx={{ color: "#fff", fontSize: { xs: "12px", sm: "14px", md: "16px", lg: "18px" } }} />
                        <span className="feedCardFooterText">{item.commentCount}</span>
                      </div>
                    </div>

                    <div className="recentVoteSwitcher">
                      <button 
                        className="switcherBtn" 
                        onClick={handlePrevVote} 
                        disabled={currentIndex === 0}
                      >
                        🠈
                      </button>
                      <span className="switcherText">{currentIndex + 1}/{allVotes.length}</span>
                      <button 
                        className="switcherBtn" 
                        onClick={handleNextVote} 
                        disabled={currentIndex === allVotes.length - 1}
                      >
                        🠊
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', paddingTop: '100px', paddingBottom: '100px' }}>
              <Loader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardRightSection;
